import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import DistrictTable from '../components/DistrictTable';
import { useDeleteDistrict } from '../hooks/useDistrictMutations';
import type { District } from '@/shared/types';
import DynamicFilterBuilder from '@/shared/filtering/DynamicFilterBuilder';
import { ADMIN_FILTER_RESOURCES, type DynamicRelationLookups } from '@/shared/filtering/types';
import { useDynamicAdminList } from '@/shared/filtering/useDynamicAdminList';
import { cityApi } from '@/features/cities/api/city.api';
import { regionApi } from '@/features/regions/api/region.api';

export default function DistrictListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState<District | null>(null);

  const list = useDynamicAdminList<District>(ADMIN_FILTER_RESOURCES.districts, { initialSortField: 'id', initialSortDir: 'asc' });
  const { data, isLoading } = list.searchQuery;
  const deleteMutation = useDeleteDistrict();

  const relationLookups = useMemo<DynamicRelationLookups>(() => ({
    'city.id': {
      fetchFn: ({ search, page, size }) => cityApi.list({ page, size, nameEn: search }),
      mapOption: (c: any) => ({ value: c.id, label: c.nameEn || c.nameAr || `#${c.id}` }),
      placeholder: t('districts.allCities'),
    },
    'region.id': {
      fetchFn: ({ search, page, size }) => regionApi.list({ page, size, nameEn: search }),
      mapOption: (r: any) => ({ value: r.id, label: r.nameEn || r.nameAr || `#${r.id}` }),
      placeholder: t('districts.allRegions'),
    },
  }), [t]);

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, { onSettled: () => setDeleteTarget(null) });
  };

  return (
    <div>
      <PageHeader title={t('districts.title')} actionLabel={t('districts.addNew')} onAction={() => navigate('/districts/create')} />
      <DynamicFilterBuilder resource={list.resource} metadata={list.metadataQuery.data} value={list.filters} onChange={list.setFilters} onReset={list.resetFilters} relationLookups={relationLookups} />
      <div style={{ marginTop: 16 }}>
        <DistrictTable
          data={data?.content || []}
          loading={isLoading}
          sortField={list.sortField}
          sortDir={list.sortDir}
          onSort={list.handleSort}
          onEdit={(id) => navigate(`/districts/${id}/edit`)}
          onDelete={(item) => setDeleteTarget(item)}
          page={data?.currentPage ?? 0}
          pageSize={list.size}
          total={data?.totalItems ?? 0}
          onPageChange={list.setPage}
        />
      </div>
      <ConfirmDialog
        open={!!deleteTarget}
        title={t('districts.deleteTitle')}
        message={`${t('districts.deleteMessage')} "${deleteTarget?.nameEn}"?`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
